/**
 * Shared active filter chips row for search modals
 *
 * Renders dismissible blue-tinted pills below the search toolbar.
 * Styling follows Add Player's tokens: no border, lighter background.
 * @module components/shared/search-modal/ActiveFilterChips/ActiveFilterChipsView
 */

import { XIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for ActiveFilterChipsView */
interface ActiveFilterChipsViewProps {
  /** Active filter chip descriptors */
  readonly chips: readonly { readonly key: string; readonly label: string }[];
  /** Pre-built removal handlers keyed by chip key */
  readonly removeHandlers: Readonly<Record<string, () => void>>;
}

/**
 * Renders dismissible active filter chips below the search toolbar.
 * @param props - Component props
 * @param props.chips - Active chip descriptors
 * @param props.removeHandlers - Per-chip removal callbacks
 */
const ActiveFilterChipsView = ({
  chips,
  removeHandlers,
}: ActiveFilterChipsViewProps) =>
  chips.length > 0 ? (
    <Stack
      direction="horizontal"
      className="flex-wrap gap-2 border-b border-outline-default px-6 py-1"
    >
      {chips.map(chip => (
        <Pressable
          key={chip.key}
          onPress={removeHandlers[chip.key]}
          className="h-7 flex-row items-center gap-1 rounded-full bg-accent-primary/15 px-3"
        >
          <Text variant="caption" className="text-accent-primary">
            {chip.label}
          </Text>
          <Icon as={XIcon} size="2xs" className="text-accent-primary" />
        </Pressable>
      ))}
    </Stack>
  ) : null;

ActiveFilterChipsView.displayName = "ActiveFilterChipsView";

export default memo(ActiveFilterChipsView);
